import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useVendorStatus } from '@/hooks/useDeepDive'
import { pageOnlyNote } from '@/lib/ws-status'
import type { MessagingWindow } from '@/lib/api/deep-dive.types'
import { MessagingDeliverySection } from '@/components/systems/MessagingDeliverySection'
import { VendorStatusCards } from '@/components/systems/VendorStatusCards'
import { SegmentedPicker } from '@/components/systems/SegmentedPicker'

/**
 * SendGrid's screen — the email path, end to end.
 *
 * Two halves, from two different feeds. `sendgrid/status` on top: the vendor's
 * own Statuspage verdict and a probe of our own credentials, which is the only
 * uptime story SendGrid has (there is no `sendgrid/overview` and no
 * `sendgrid/incidents`). Then the delivery block from `sendgrid/messaging/*` —
 * sent, bounce rate, deliverability, spam reports, the hourly chart and the
 * recent bounces.
 *
 * Email figures live here now. They used to be fetched from
 * `twilio/messaging/*?channel=email` and read on the Twilio screen, which put
 * SendGrid's numbers under Twilio's name; the backend has since given each
 * vendor its own family, so this screen asks for its own data and the Twilio
 * screen is SMS only.
 *
 * Three things in the design have no data behind them and are not invented:
 *
 * · **Avg delivery time and open rate.** No field carries either and neither is
 *   derivable, so they are not rendered — beside live figures a plausible
 *   constant is indistinguishable from a measurement.
 * · **The "has been down for" counter and estimated losses.** There is no outage
 *   feed on this path — `sendgrid/status` reports vendor health but no outage
 *   start — so a live-ticking counter would have had nothing behind it, and an
 *   invented loss figure is worse than no figure.
 *
 * The grey note under the cards is the one thing `sendgrid/status` says that the
 * cards cannot: the shared Twilio/SendGrid status page is degraded about
 * products OWE never calls. Without it this screen reads green while
 * status.sendgrid.com reads amber, and the board looks wrong.
 */
export function SendGridServiceManagement() {
  const [window, setWindow] = useState<MessagingWindow>('24h')

  const vendorStatus = useVendorStatus('sendgrid')
  const offScope = pageOnlyNote((vendorStatus.data ?? {}) as Record<string, unknown>)

  return (
    <div className="space-y-6 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-fg text-3xl leading-tight font-semibold tracking-tight sm:text-[32px]">
            SendGrid
          </h1>
          <p className="text-fg-muted mt-1 text-sm">
            Battery-calculator email delivery, and this deployment's own account.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedPicker value={window} onChange={setWindow} label="Window" />
          <a
            href="https://status.sendgrid.com/"
            target="_blank"
            rel="noreferrer noopener"
            className="border-line-bright text-fg-muted hover:bg-surface-3 hover:text-fg flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase transition-colors"
          >
            <ExternalLink className="size-3.5" />
            Status page
          </a>
        </div>
      </div>

      <VendorStatusCards
        payload={vendorStatus.data}
        isLoading={vendorStatus.isLoading}
        error={vendorStatus.error}
        onRetry={() => void vendorStatus.refetch()}
      />

      {/* The vendor reports trouble, but in products we do not use. Grey and
          quiet: it explains a discrepancy someone may spot against
          status.sendgrid.com, and is never this screen's own state. */}
      {offScope && (
        <p className="border-line bg-surface text-fg-muted rounded-lg border px-4 py-2.5 text-sm italic">
          {offScope}
        </p>
      )}

      <MessagingDeliverySection provider="sendgrid" window={window} />
    </div>
  )
}
